import {
  BATCH_MAX_HEIGHT,
  CONTAINER_PADDING,
  MAX_CONTAINER_HEIGHT,
  MIN_CONTAINER_HEIGHT,
  NODE_TO_CARD_GAP,
  NODE_TO_LABEL_GAP,
  PERIOD_LABEL_HEIGHT,
  PERIOD_SPACING,
  RULER_LINE_COLOR,
  RULER_SECTION_LINE_COLOR,
  SECTION_COLORS,
  SECTION_DIVIDER_COLOR,
  SECTION_HEADER_HEIGHT,
  TIMELINE_VERTICAL_PADDING,
  TRACK_HIGHLIGHT_WIDTH,
} from "./timeline-constants";
import {
  TIMELINE_DOT_STATE,
  type TimelineDotState,
} from "./timeline-period-selectors";
import type { ParsedTimeline, TimelineSection } from "./timeline-types";
import type {
  SnapCandidate,
  TimelineContainerMetrics,
  TimelineLayoutMetrics,
} from "./timeline-root-types";

const TRACK_HIGHLIGHT_HEX_ALPHA = "18";
const NEEDLE_DOT_SHADOW_HEX_ALPHA = "40";

interface TimelineStackMetrics {
  hasAboveLabels: boolean;
  hasBelowLabels: boolean;
  maxAboveStack: number;
  maxBelowStack: number;
}

interface TimelineStackSpaces {
  aboveCardsSpace: number;
  aboveLabelSpace: number;
  belowCardsSpace: number;
  belowLabelSpace: number;
}

export interface TimelineRulerLineModel {
  background: string;
  height: number;
  left: number;
}

export interface TimelineSectionFrameModel {
  borderRight: string | undefined;
  height: number;
  left: number;
  width: number;
}

function selectTimelineStackHeight(input: { eventCount: number }): number {
  return input.eventCount > 0 ? BATCH_MAX_HEIGHT : 0;
}

function getInitialStackMetrics(): TimelineStackMetrics {
  return {
    hasAboveLabels: false,
    hasBelowLabels: false,
    maxAboveStack: 0,
    maxBelowStack: 0,
  };
}

function selectTimelineStackMetricsForPeriod(input: {
  collapsedSections: Set<number>;
  metrics: TimelineStackMetrics;
  period: ParsedTimeline["periods"][number];
}): TimelineStackMetrics {
  if (input.collapsedSections.has(input.period.sectionIndex)) {
    return input.metrics;
  }

  const isAbove = input.period.globalIndex % 2 === 1;
  const stackHeight = selectTimelineStackHeight({
    eventCount: input.period.events.length,
  });

  if (isAbove) {
    return {
      ...input.metrics,
      hasBelowLabels: true,
      maxAboveStack: Math.max(input.metrics.maxAboveStack, stackHeight),
    };
  }

  return {
    ...input.metrics,
    hasAboveLabels: true,
    maxBelowStack: Math.max(input.metrics.maxBelowStack, stackHeight),
  };
}

function selectTimelineStackMetrics(input: {
  collapsedSections: Set<number>;
  parsed: ParsedTimeline;
}): TimelineStackMetrics {
  return input.parsed.periods.reduce<TimelineStackMetrics>(
    (metrics, period) =>
      selectTimelineStackMetricsForPeriod({
        collapsedSections: input.collapsedSections,
        metrics,
        period,
      }),
    getInitialStackMetrics(),
  );
}

function selectTimelineStackSpaces(input: {
  stackMetrics: TimelineStackMetrics;
}): TimelineStackSpaces {
  return {
    aboveCardsSpace:
      input.stackMetrics.maxAboveStack > 0
        ? NODE_TO_CARD_GAP + input.stackMetrics.maxAboveStack
        : 0,
    belowCardsSpace:
      input.stackMetrics.maxBelowStack > 0
        ? NODE_TO_CARD_GAP + input.stackMetrics.maxBelowStack
        : 0,
    aboveLabelSpace: input.stackMetrics.hasAboveLabels
      ? NODE_TO_LABEL_GAP + PERIOD_LABEL_HEIGHT
      : 0,
    belowLabelSpace: input.stackMetrics.hasBelowLabels
      ? NODE_TO_LABEL_GAP + PERIOD_LABEL_HEIGHT
      : 0,
  };
}

function selectTimelineTrackY(input: { stackSpaces: TimelineStackSpaces }): number {
  const aboveSpace = Math.max(
    input.stackSpaces.aboveCardsSpace,
    input.stackSpaces.aboveLabelSpace,
  );

  return SECTION_HEADER_HEIGHT + TIMELINE_VERTICAL_PADDING + aboveSpace;
}

function selectTimelineChartHeight(input: {
  stackSpaces: TimelineStackSpaces;
  trackY: number;
}): number {
  const belowSpace = Math.max(
    input.stackSpaces.belowCardsSpace,
    input.stackSpaces.belowLabelSpace,
  );

  return input.trackY + belowSpace + TIMELINE_VERTICAL_PADDING;
}

function appendTimelineHexAlpha(input: {
  color: string;
  alphaHex: string;
}): string {
  const normalized = input.color.trim();
  if (/^#[\dA-Fa-f]{6}$/.test(normalized)) {
    return `${normalized}${input.alphaHex}`;
  }

  return input.color;
}

export function selectTimelineSectionColor(input: {
  sectionIndex: number;
}): string {
  return SECTION_COLORS[input.sectionIndex % SECTION_COLORS.length];
}

export function selectTimelineLayoutMetrics(input: {
  collapsedSections: Set<number>;
  parsed: ParsedTimeline | null;
}): TimelineLayoutMetrics | null {
  if (!input.parsed || input.parsed.periods.length === 0) {
    return null;
  }

  const stackMetrics = selectTimelineStackMetrics({
    collapsedSections: input.collapsedSections,
    parsed: input.parsed,
  });
  const stackSpaces = selectTimelineStackSpaces({ stackMetrics });
  const trackY = selectTimelineTrackY({ stackSpaces });

  return {
    trackY,
    chartHeight: selectTimelineChartHeight({ stackSpaces, trackY }),
    contentWidth: Math.max(
      PERIOD_SPACING,
      input.parsed.periods.length * PERIOD_SPACING,
    ),
  };
}

export function selectTimelineSidePad(input: {
  layout: TimelineLayoutMetrics | null;
  viewportWidth: number;
}): number {
  if (!input.layout || input.viewportWidth <= 0) {
    return 0;
  }

  if (input.layout.contentWidth <= input.viewportWidth) {
    return (input.viewportWidth - input.layout.contentWidth) / 2;
  }

  return Math.max(0, (input.viewportWidth - PERIOD_SPACING) / 2);
}

export function selectTimelineSnapCandidates(input: {
  collapsedSections: Set<number>;
  parsed: ParsedTimeline | null;
}): SnapCandidate[] {
  if (!input.parsed) {
    return [];
  }

  return input.parsed.periods
    .filter((period) => !input.collapsedSections.has(period.sectionIndex))
    .map((period) => ({
      index: period.globalIndex,
      centerX: period.globalIndex * PERIOD_SPACING + PERIOD_SPACING / 2,
      sectionColor: selectTimelineSectionColor({
        sectionIndex: period.sectionIndex,
      }),
    }));
}

export function findNearestTimelineSnapCandidate(input: {
  candidates: SnapCandidate[];
  x: number;
}): SnapCandidate | null {
  const [firstCandidate] = input.candidates;
  if (!firstCandidate) {
    return null;
  }

  return input.candidates.reduce<SnapCandidate>((closest, candidate) => {
    const closestDistance = Math.abs(closest.centerX - input.x);
    const candidateDistance = Math.abs(candidate.centerX - input.x);
    return candidateDistance < closestDistance ? candidate : closest;
  }, firstCandidate);
}

export function selectTimelineSnappedSectionColor(input: {
  snapCandidates: SnapCandidate[];
  snappedPeriodIndex: number | null;
}): string {
  if (input.snappedPeriodIndex != null) {
    const snappedCandidate = input.snapCandidates.find(
      (candidate) => candidate.index === input.snappedPeriodIndex,
    );

    if (snappedCandidate) {
      return snappedCandidate.sectionColor;
    }
  }

  return input.snapCandidates[0]?.sectionColor ?? SECTION_COLORS[0];
}

export function selectTimelineSectionBoundaryIndices(input: {
  parsed: ParsedTimeline | null;
}): Set<number> {
  if (!input.parsed) {
    return new Set<number>();
  }

  return new Set<number>(
    input.parsed.sections.map((section) => section.periodStartIndex),
  );
}

export function selectTimelineTrackHighlightGradient(input: {
  sectionColor: string;
}): string {
  return `radial-gradient(ellipse ${TRACK_HIGHLIGHT_WIDTH / 2}px 6px at center, ${appendTimelineHexAlpha(
    {
      color: input.sectionColor,
      alphaHex: TRACK_HIGHLIGHT_HEX_ALPHA,
    },
  )} 0%, transparent 100%)`;
}

export function selectTimelineNeedleDotShadow(input: {
  sectionColor: string;
}): string {
  return `0 0 6px ${appendTimelineHexAlpha({
    color: input.sectionColor,
    alphaHex: NEEDLE_DOT_SHADOW_HEX_ALPHA,
  })}`;
}

export function selectTimelineTotalEvents(input: {
  parsed: ParsedTimeline | null;
}): number {
  if (!input.parsed) {
    return 0;
  }

  return input.parsed.periods.reduce(
    (eventCount, period) => eventCount + period.events.length,
    0,
  );
}

export function selectTimelineContainerMetrics(input: {
  chartHeight: number;
}): TimelineContainerMetrics {
  const chromeHeight = CONTAINER_PADDING * 2;
  const containerHeight = Math.max(
    MIN_CONTAINER_HEIGHT,
    Math.min(MAX_CONTAINER_HEIGHT, input.chartHeight + chromeHeight),
  );

  return {
    containerHeight,
    viewportHeight: Math.max(120, containerHeight - chromeHeight),
  };
}

export function selectTimelineDotState(input: {
  centeredPeriodIndex: number;
  periodGlobalIndex: number;
}): TimelineDotState {
  if (input.periodGlobalIndex < input.centeredPeriodIndex) {
    return TIMELINE_DOT_STATE.Past;
  }

  if (input.periodGlobalIndex === input.centeredPeriodIndex) {
    return TIMELINE_DOT_STATE.Now;
  }

  return TIMELINE_DOT_STATE.Future;
}

export function selectTimelineRulerLineModel(input: {
  chartHeight: number;
  periodGlobalIndex: number;
  sectionBoundaryIndices: Set<number>;
}): TimelineRulerLineModel {
  return {
    background: input.sectionBoundaryIndices.has(input.periodGlobalIndex)
      ? RULER_SECTION_LINE_COLOR
      : RULER_LINE_COLOR,
    height: input.chartHeight,
    left: input.periodGlobalIndex * PERIOD_SPACING + PERIOD_SPACING / 2 - 0.5,
  };
}

export function selectTimelineSectionFrameModel(input: {
  chartHeight: number;
  section: TimelineSection;
  sectionCount: number;
  sectionRenderIndex: number;
}): TimelineSectionFrameModel {
  return {
    borderRight:
      input.sectionRenderIndex < input.sectionCount - 1
        ? `1px solid ${SECTION_DIVIDER_COLOR}`
        : undefined,
    height: input.chartHeight,
    left: input.section.periodStartIndex * PERIOD_SPACING,
    width: Math.max(
      PERIOD_SPACING,
      input.section.periodCount * PERIOD_SPACING,
    ),
  };
}
